/**
 * ValidateGroup - Validator for group and multi-select elements.
 * Provides validation for field groups with min/max count constraints.
 */

// Declare external dependencies

// Module flag
var _VALIDATE_GROUP_: boolean = true;

// Debug flag - declare globally if not already defined
if (typeof (globalThis as any).debug === 'undefined') {
	(globalThis as any).debug = false;
}
var debug: boolean = (globalThis as any).debug;

// Get FEW functions from globalThis (defined in errorreporter.ts)
const setValidationFEW: (this: FieldGroupInstance, ...args: any[]) => void = (globalThis as any).setValidationFEW;
const setRequiredFEW: (this: FieldGroupInstance, required: boolean) => void = (globalThis as any).setRequiredFEW;
const validateFEW: (this: FieldGroupInstance) => boolean = (globalThis as any).validateFEW;
const validateSiblingFEW: (this: FieldGroupInstance, ...args: any[]) => boolean = (globalThis as any).validateSiblingFEW;
const showStatusFEW: (this: FieldGroupInstance) => void = (globalThis as any).showStatusFEW;
const addEventFEW: (this: FieldGroupInstance, ...args: any[]) => void = (globalThis as any).addEventFEW;
const registerEventFEW: (this: FieldGroupInstance, code: string, event: string) => void = (globalThis as any).registerEventFEW;
const forceErrorFEW: (this: FieldGroupInstance, ...args: any[]) => void = (globalThis as any).forceErrorFEW;
const getSiblingFEW: (this: FieldGroupInstance, name: string) => FieldGroupElement | null = (globalThis as any).getSiblingFEW;
const setMessageFEW: (this: FieldGroupInstance, message: string) => boolean = (globalThis as any).setMessageFEW;
const compareNullableFEW: (this: FieldGroupInstance, ...args: any[]) => number | null = (globalThis as any).compareNullableFEW;
const hasChangedFEW: (this: FieldGroupInstance) => boolean = (globalThis as any).hasChangedFEW;
const initFEW: (this: FieldGroupInstance) => void = (globalThis as any).initFEW;

/** Form object interface */
interface FormObjectFG {
	fieldErrorRequiredRed: { src: string };
	fieldErrorRequiredBlack: { src: string };
	fieldErrorOff: { src: string };
	fieldErrorOn: { src: string };
}

/** Element within a field group */
interface FieldGroupElement {
	name: string;
	type: string;
	isValid: boolean;
	required: boolean;
	image?: HTMLImageElement;
	message?: string;
	formObj: FormObjectFG;
	get: () => string;
	set: (val: string) => void;
	focus: () => void;
	compare: (value: string) => number | null;
	validate: () => boolean;
	showStatus: () => void;
	setRequired: (required: boolean) => void;
	executeEvent: (type: string) => void;
	registerEvent: (code: string | (() => void), event: string) => void;
	getSibling: (name: string) => FieldGroupInstance | null;
	isCollapsed: () => boolean;
	reset: () => void;
}

/** Event callback type */
type FieldGroupEventCallback = (() => void) | string;

/** Event registration object */
interface FieldGroupEvent {
	type: string;
	code: FieldGroupEventCallback;
}

/** Field group instance interface */
interface FieldGroupInstance {
	formObj: FormObjectFG;
	name: string;
	type: string;
	events: FieldGroupEvent[];
	element: FieldGroupElement[];
	trappedElements: number;
	minCount: number | null;
	maxCount?: number | null;
	isValid: boolean;
	containsSimpleFields: boolean;
	required?: boolean;
	regChangeEvent?: boolean;
	curElement: FieldGroupElement | null;
	lastVal?: string;
	message?: string;
	image?: HTMLImageElement;
	onErrorChildStatus?: string;

	// Methods
	get: (which?: string) => string;
	set: (val: string, which?: string | number) => void;
	setAll: (val: string) => void;
	numSelected: () => number;
	addElement: (elementName: string) => void;
	focus: () => void;
	trapEvents: () => void;
	trapEvent: (event: string, saveAsEvent: string, which: number) => void;
	handleEvent: (element: FieldGroupElement, type: string) => void;
	setChildStatus: (whichRequired: string | boolean, doValidation: boolean | string, onErrorChildStatus: string) => void;
	compare: (value: string) => number | null;
	executeEvent: (type: string, which?: number) => void;
	isCollapsed: () => boolean;
	reset: () => void;

	// Inherited from FormElementWrapper
	setValidation: (...args: any[]) => void;
	setRequired: (required: boolean) => void;
	validate: () => boolean;
	validateSibling: (...args: any[]) => boolean;
	showStatus: () => void;
	addEvent: (...args: any[]) => void;
	registerEvent: (code: FieldGroupEventCallback, event: string) => void;
	forceError: (...args: any[]) => void;
	getSibling: (name: string) => FieldGroupElement | null;
	setMessage: (message: string) => boolean;
	compareNullable: (...args: any[]) => number | null;
	hasChanged: () => boolean;
	init: () => void;
}

/** FormValidator class interface */
interface FormValidatorClass {
	prototype: {
		childElements: { [name: string]: FieldGroupInstance | undefined };
		createGroup: (name: string, ...elementNames: string[]) => FieldGroupInstance | null;
	};
}

//================== validator for group and multi-select elements ==================

/**
 * Validate count constraints for a group element.
 * Checks minCount and maxCount against the number of selected items.
 */
function validateCount(this: FieldGroupInstance): boolean {
	if (this.numSelected == null || typeof (this.numSelected) != "function") return true;
	if (this.regChangeEvent != true && !this.required) {
		var self = this;
		this.registerEvent(function() {
			if (!self.required && self.numSelected() == 0) {
				self.setChildStatus('none', true, 'selected');
			}
		}, "change");
	}
	var count = this.numSelected();

	if (this.type == "group" && this.minCount != null) {
		var doValidation: boolean | string = (this.curElement == null) ? true : this.curElement.name;
		if (count < this.minCount)
			this.setChildStatus("all", doValidation, "none");
		else if (count > this.minCount)
			this.setChildStatus("none", doValidation, "selected");
		else
			this.setChildStatus("selected", doValidation, "selected");
	}

	var items = (this.type == "group" && !this.containsSimpleFields) ? " field" : " item";
	var selected = (this.type == "group" && !this.containsSimpleFields) ? "completed" : "selected";
	if (this.minCount != null && count < this.minCount) return this.setMessage("At least " + this.minCount + items + (this.minCount > 1 ? "s" : "") + " must be " + selected);
	if (this.maxCount != null && count > this.maxCount) return this.setMessage("No more than " + this.maxCount + items + (this.maxCount > 1 ? "s" : "") + " may be " + selected);
	return true;
}

/**
 * Validate that all members of the group are valid.
 */
function validateMembersValid(this: FieldGroupInstance): boolean {
	var valid: boolean | number = true;
	if (this.type == "group")
		for (var i in this.element)
			valid = (valid as number) & (this.element[i].isValid ? 1 : 0);
	return !!valid;
}

/**
 * Validate all-or-nothing constraint - either all fields must be filled or none.
 */
function validateAllOrNothing(this: FieldGroupInstance): boolean {
	if (this.numSelected == null || typeof (this.numSelected) != "function") return true;
	var count = this.numSelected();
	this.minCount = (count == 0) ? 0 : this.element.length;

	if (this.type == "group") {
		this.setChildStatus((count == 0) ? "none" : "all", (this.curElement == null) ? true : this.curElement.name, "none");
	}

	if (count == 0 || count == this.element.length) return true;
	var items = (this.type == "group" && !this.containsSimpleFields) ? " fields" : " items";
	var selected = (this.type == "group" && !this.containsSimpleFields) ? "completed" : "selected";
	return this.setMessage("Either all" + items + " must be " + selected + " or no" + items + " may be " + selected);
}

//================== FieldGroup class ==================

/**
 * FieldGroup constructor - creates a group of related form fields.
 * @param formObj - The form validator object
 * @param elementName - The name of the field group
 */
function FieldGroup(this: FieldGroupInstance, formObj: FormObjectFG, elementName: string): void {
	if (debug) window.status = "Adding " + elementName;

	this.formObj = formObj;
	this.name = elementName;
	this.type = "group";
	this.events = [];
	this.element = [];
	this.trappedElements = 0;
	this.minCount = 1;
	this.isValid = true;
	this.containsSimpleFields = true;
	this.curElement = null;

	this.get = getFG;
	this.set = setFG;
	this.setAll = setAllFG;
	this.numSelected = numSelectedFG;
	this.addElement = addElementFG;
	this.focus = focusFG;
	this.trapEvents = trapEventsFG;
	this.trapEvent = trapEventFG;
	this.handleEvent = handleEventFG;
	this.setChildStatus = setChildStatusFG;
	this.compare = compareFG;
	this.executeEvent = executeEventFG;
	this.isCollapsed = isCollapsedFG;
	this.reset = resetFG;

	this.setValidation = setValidationFEW;
	this.setRequired = setRequiredFEW;
	this.validate = validateFEW;
	this.validateSibling = validateSiblingFEW;
	this.showStatus = showStatusFEW;
	this.addEvent = addEventFEW;
	this.registerEvent = registerEventFEW;
	this.forceError = forceErrorFEW;
	this.getSibling = getSiblingFEW;
	this.setMessage = setMessageFEW;
	this.compareNullable = compareNullableFEW;
	this.hasChanged = hasChangedFEW;
	this.init = initFEW;

	this.init();

	if (this.image == null) {
		this.showStatus = showStatusFG;
		this.onErrorChildStatus = "none";
	}
}

/**
 * Get value(s) from the field group.
 * @param which - Optional name of specific element to get
 * @returns The value(s) of the group
 */
function getFG(this: FieldGroupInstance, which?: string): string {
	if (which != null) {
		for (var i in this.element)
			if (this.element[i].name == which)
				return this.element[i].get();
		return "";
	}
	var out = "", hasValue = false, elementVal: string;
	for (var i in this.element) {
		elementVal = this.element[i].get();
		out += VALUE_SPACER + elementVal;
		if (elementVal != null && elementVal != "")
			hasValue = true;
	}
	if (!hasValue) return "";
	return out.substring(1);
}

/**
 * Set value(s) in the field group.
 * @param val - The value to set
 * @param which - Optional name or index of specific element to set
 */
function setFG(this: FieldGroupInstance, val: string, which?: string | number): void {
	if (which != null) {
		if (typeof (which) == "number")
			return this.element[which].set(val);
		for (var idx in this.element)
			if (this.element[idx].name == which)
				return this.element[idx].set(val);
		return;
	}
	var values = getValues(val);
	for (var j = 0; j < this.element.length; j++)
		this.element[j].set(j < values.length ? values[j] : "");
}

/**
 * Set the same value for all elements in the group.
 * @param val - The value to set for all elements
 */
function setAllFG(this: FieldGroupInstance, val: string): void {
	for (var i in this.element) {
		this.element[i].set(val);
	}
}

/**
 * Count the number of selected/filled elements in the group.
 * @returns The number of elements with non-empty values
 */
function numSelectedFG(this: FieldGroupInstance): number {
	var numSelected = 0;
	for (var i in this.element) {
		if (this.element[i].get() != null && this.element[i].get() != "")
			numSelected++;
	}
	return numSelected;
}

/**
 * Add an element to the field group.
 * @param elementName - The name of the element to add
 */
function addElementFG(this: FieldGroupInstance, elementName: string): void {
	var element = this.getSibling(elementName);
	if (element == null) return;
	this.element.push(element);
	this.trapEvents();
	this.lastVal = this.get();
	this.containsSimpleFields = this.containsSimpleFields && (element.type == "radio" || element.type == "checkbox");
}

/**
 * Focus the first element in the group.
 */
function focusFG(this: FieldGroupInstance): void {
	if (this.element.length > 0)
		this.element[0].focus();
}

/**
 * Trap events for all elements in the group.
 */
function trapEventsFG(this: FieldGroupInstance): void {
	while (this.trappedElements < this.element.length) {
		this.trapEvent("focus", "", this.trappedElements);
		this.trapEvent("blur", "", this.trappedElements);
		this.trapEvent("change", "", this.trappedElements);
		this.trappedElements++;
	}
}

/**
 * Trap a specific event for an element.
 * @param event - The event type to trap
 * @param saveAsEvent - Not used
 * @param which - The index of the element
 */
function trapEventFG(this: FieldGroupInstance, event: string, saveAsEvent: string, which: number): void {
	if (which == null) return;
	var self = this;
	var eventType = event;
	this.element[which].registerEvent(function() {
		var thisObj = self.element[which];
		var sibling = thisObj.getSibling(self.name);
		if (sibling) {
			sibling.handleEvent(thisObj, eventType);
		}
	}, event);
}

/**
 * Set the required status of child elements.
 * @param whichRequired - Which elements should be required ("all", "none", "selected", or element name)
 * @param doValidation - Whether to run validation (true, false, or element name)
 * @param onErrorChildStatus - Status for children on error
 */
function setChildStatusFG(this: FieldGroupInstance, whichRequired: string | boolean, doValidation: boolean | string, onErrorChildStatus: string): void {
	this.onErrorChildStatus = onErrorChildStatus;
	for (var i = 0; i < this.element.length; i++) {
		if (whichRequired == "selected")
			this.element[i].setRequired(this.element[i].get() != "");
		else if (whichRequired == "all" || whichRequired == "none")
			this.element[i].setRequired(whichRequired == "all");
		else if (whichRequired)
			this.element[i].setRequired(whichRequired == this.element[i].name);

		if (doValidation == true || doValidation == this.element[i].name || !this.element[i].isValid)
			this.element[i].validate();
		else
			this.element[i].showStatus();
	}
}

/**
 * Execute an event for elements in the group.
 * @param type - The event type
 * @param which - Optional index of specific element
 */
function executeEventFG(this: FieldGroupInstance, type: string, which?: number): void {
	if (which != null) return this.element[which].executeEvent(type);
	for (var i in this.element)
		this.element[i].executeEvent(type);
}

/**
 * Handle an event from a child element.
 * @param element - The element that triggered the event
 * @param type - The event type
 */
function handleEventFG(this: FieldGroupInstance, element: FieldGroupElement, type: string): void {
	this.curElement = element;
	var thisObj = this;
	var events = this.events;
	var fn = (window as any)["generic_" + type];
	if (typeof fn == "function") fn(thisObj);
	for (var i in events)
		if (type == events[i].type) {
			var code = events[i].code;
			if (typeof code === "function") {
				if (debug) window.status = thisObj.name + "; " + type + "; Executing callback";
				code();
			} else {
				throw new Error("registerEvent no longer accepts strings. Pass a callback function instead.");
			}
		}
	this.curElement = null;
}

/**
 * Show validation status for the group.
 */
function showStatusFG(this: FieldGroupInstance): void {
	if (this.onErrorChildStatus != "none") {
		var doAll = this.onErrorChildStatus == "all", e: FieldGroupElement;
		for (var i in this.element) {
			e = this.element[i];
			if (e.image && (doAll || (e.get() != null && e.get() != "")) && e.isValid) {
				e.image.src = (this.isValid && e.isValid) ? (e.required ? (e.get() == "" ? e.formObj.fieldErrorRequiredRed.src : e.formObj.fieldErrorRequiredBlack.src) : e.formObj.fieldErrorOff.src) : e.formObj.fieldErrorOn.src;
				e.image.alt = this.message || "";
				e.image.title = this.message || "";
				e.message = this.message;
			}
		}
	}
	if (this.message && this.message > "") window.status = this.message;
}

/**
 * Compare the group's value with another value.
 * @param value - The value to compare against
 * @returns -1, 0, 1, or null
 */
function compareFG(this: FieldGroupInstance, value: string): number | null {
	var values = getValues(value);
	if (values.length == 0)
		while (values.length < this.element.length)
			values[values.length] = "";
	else if (values.length != this.element.length)
		return null;

	var less = false, greater = false, curCmp: number | null;
	for (var i = 0; i < values.length; i++) {
		curCmp = this.element[i].compare(values[i]);
		if (curCmp != null && curCmp < 0) less = true;
		if (curCmp != null && curCmp > 0) greater = true;
	}
	if (less && greater) return null;
	if (less) return -1;
	if (greater) return 1;
	return 0;
}

/**
 * Check if the field group is collapsed (hidden).
 * @returns true if collapsed, false otherwise
 */
function isCollapsedFG(this: FieldGroupInstance): boolean {
	var t = this, foundEle = false;
	for (var i in t.element) {
		var e = t.element[i];
		if (e.type != "hidden" || e.image) {
			foundEle = true;
			if (!e.isCollapsed()) return false;
		}
	}
	return foundEle || (t.image == null || !t.image.checkVisibility());
}

/**
 * Reset all elements in the group.
 */
function resetFG(this: FieldGroupInstance): void {
	var t = this;
	for (var i in t.element) {
		t.element[i].reset();
	}
}

/**
 * Parse a value string into an array of values.
 * @param val - The value string to parse
 * @returns Array of values
 */
function getValues(val: string): string[] {
	var values: string[] = [], curVal = -1;
	if (val > "") {
		values[++curVal] = "";
		for (var i = 0; i < val.length; i++) {
			var c = val.charAt(i);
			if (c == VALUE_SPACER)
				values[++curVal] = "";
			else
				values[curVal] += c;
		}
	}
	return values;
}

//------------------ extension to FormValidator ------------------

FormValidator.prototype.createGroup = function (this: { childElements: { [name: string]: FieldGroupInstance | undefined } }, name: string, ...elementNames: string[]): FieldGroupInstance | null {
	if (this.childElements[name] != null) return null;
	var newElement = new (FieldGroup as any)(this, name) as FieldGroupInstance;
	this.childElements[name] = newElement;
	for (var i = 0; i < elementNames.length; i++)
		newElement.addElement(elementNames[i]);

	return newElement;
};

// Export to globalThis for backwards compatibility
(globalThis as any)._VALIDATE_GROUP_ = _VALIDATE_GROUP_;
(globalThis as any).validateCount = validateCount;
(globalThis as any).validateMembersValid = validateMembersValid;
(globalThis as any).validateAllOrNothing = validateAllOrNothing;
(globalThis as any).FieldGroup = FieldGroup;
(globalThis as any).getFG = getFG;
(globalThis as any).setFG = setFG;
(globalThis as any).setAllFG = setAllFG;
(globalThis as any).numSelectedFG = numSelectedFG;
(globalThis as any).addElementFG = addElementFG;
(globalThis as any).focusFG = focusFG;
(globalThis as any).trapEventsFG = trapEventsFG;
(globalThis as any).trapEventFG = trapEventFG;
(globalThis as any).setChildStatusFG = setChildStatusFG;
(globalThis as any).executeEventFG = executeEventFG;
(globalThis as any).handleEventFG = handleEventFG;
(globalThis as any).showStatusFG = showStatusFG;
(globalThis as any).compareFG = compareFG;
(globalThis as any).isCollapsedFG = isCollapsedFG;
(globalThis as any).resetFG = resetFG;
(globalThis as any).getValues = getValues;

if (_SR_ != null) _SR_.notify("validategroup.js");

// ES module exports
export {
	_VALIDATE_GROUP_,
	validateCount,
	validateMembersValid,
	validateAllOrNothing,
	FieldGroup,
	getFG,
	setFG,
	setAllFG,
	numSelectedFG,
	addElementFG,
	focusFG,
	trapEventsFG,
	trapEventFG,
	setChildStatusFG,
	executeEventFG,
	handleEventFG,
	showStatusFG,
	compareFG,
	isCollapsedFG,
	resetFG,
	getValues
};
export type { FieldGroupInstance, FieldGroupElement, FieldGroupEvent, FormObjectFG };
